import React, { useState, useEffect, useRef } from 'react';
import { TabBar } from '../TabManager';
import useColumnResize from '../../hooks/useColumnResize';
import JalaliDatePicker from '../../components/JalaliDatePicker';

/* ── تبدیل تاریخ شمسی↔میلادی ── */
function jalaliToGregorian(jStr) {
  if (!jStr || !jStr.includes('/')) return '';
  const p = jStr.split('/').map(Number);
  if (p.length !== 3 || !p[0]) return '';
  let jy = p[0]+1595;
  let days = -355779 + 365*jy + Math.floor(jy/33)*8 + Math.floor(((jy%33)+3)/4) + p[2];
  for (let i=1; i<p[1]; i++) days += i<=6 ? 31 : 30;
  let gy = 400*Math.floor(days/146097); days %= 146097;
  if (days>36524) { gy += 100*Math.floor(--days/36524); days %= 36524; if (days>=365) days++; }
  gy += 4*Math.floor(days/1461); days %= 1461;
  if (days>365) { gy += Math.floor((days-1)/365); days = (days-1)%365; }
  let gd = days+1;
  const m = [0,31,29,31,30,31,30,31,31,30,31,30,31];
  if ((gy%4!==0)||(gy%100===0 && gy%400!==0)) m[2]=28;
  let gm;
  for (gm=1; gm<=12 && gd>m[gm]; gm++) gd -= m[gm];
  return `${gy}-${String(gm).padStart(2,'0')}-${String(gd).padStart(2,'0')}`;
}

function gregorianToJalali(gStr) {
  if (!gStr) return '';
  const d = new Date(gStr);
  if (isNaN(d)) return '';
  const gy=d.getFullYear(), gm=d.getMonth()+1, gd=d.getDate();
  let jy = gy<=1600 ? 0 : 979;
  let y = gy-(gy<=1600?621:1600);
  let gYm = gm-2;
  if (gYm<=0) { gYm+=12; y--; }
  let g_dn = 365*y + Math.floor((y+3)/4) - Math.floor((y+99)/100) + Math.floor((y+399)/400);
  for (let i=0; i<gYm-1; i++) g_dn += [31,28,31,30,31,30,31,31,30,31,30,31][i];
  if (gYm>1 && ((y%4===0 && y%100!==0)||y%400===0)) g_dn++;
  g_dn += gd-1;
  let j_dn = g_dn-79;
  let j_np = Math.floor(j_dn/12053); j_dn %= 12053;
  jy += 33*j_np + 4*Math.floor(j_dn/1461);
  j_dn %= 1461;
  if (j_dn>=366) { jy += Math.floor((j_dn-1)/365); j_dn = (j_dn-1)%365; }
  let jm,jd;
  const ms = [31,31,31,31,31,31,30,30,30,30,30,29];
  for (jm=0; jm<11 && j_dn>=ms[jm]; jm++) j_dn -= ms[jm];
  jd = j_dn+1;
  return `${jy}/${String(jm+1).padStart(2,'0')}/${String(jd).padStart(2,'0')}`;
}

function isoToJalaliDisplay(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const jDate = gregorianToJalali(iso);
  const time = d.toLocaleTimeString('fa-IR', { hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false });
  const jParts = jDate.split('/').map(n => String(n).replace(/[0-9]/g, d=>'۰۱۲۳۴۵۶۷۸۹'[d]));
  return `${jParts.join('/')}  ${time}`;
}

const toFa = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);

const EVENT_LABELS = {
  LOGIN_OK:'ورود موفق', LOGIN_FAIL:'ورود ناموفق', LOGIN_EXPIRED:'ورود منقضی',
  LOGOUT:'خروج', LOCKED:'قفل حساب', DISP_LOCK:'قفل پینگ‌پنگ',
  PERM_DENIED:'دسترسی رد شد', DOC_CREATE:'ایجاد سند', DOC_UPDATE:'ویرایش سند',
  DOC_DELETE:'حذف سند', DETAIL_CREATE:'ایجاد قلم', DETAIL_UPDATE:'ویرایش قلم',
  DETAIL_DELETE:'حذف قلم', SESSION_REVOKE:'ابطال نشست', SESSION_DISP:'جابجایی نشست', OTHER:'سایر',
};
const RESULT_COLORS = { SUCCESS:'#2e7d32', FAILED:'#c62828', LOCKED:'#e65100', DISPLACEMENT_LOCKED:'#b71c1c', EXPIRED:'#f57f17' };
const EVENT_COLORS = { LOGIN_OK:'#e8f5e9', LOGIN_FAIL:'#ffebee', LOGOUT:'#e3f2fd', LOCKED:'#fff3e0', DISP_LOCK:'#fce4ec', PERM_DENIED:'#fff8e1', SESSION_REVOKE:'#f3e5f5', SESSION_DISP:'#fce4ec' };

export default function AdminAuditLog({ user, onBack }) {
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [pages, setPages]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [stats, setStats]     = useState(null);

  const [fEvent, setFEvent]       = useState('');
  const [fUsername, setFUsername]  = useState('');
  const [fIp, setFIp]             = useState('');
  const [fResult, setFResult]     = useState('');
  const [fDateFrom, setFDateFrom] = useState(''); // شمسی "1405/04/01"
  const [fDateTo, setFDateTo]     = useState('');   // شمسی
  const [fSearch, setFSearch]     = useState('');
  const [sort, setSort]           = useState('-timestamp');
  const [pageSize] = useState(50);

  const { widths: colW, TH } = useColumnResize([55, 160, 100, 90, 120, 90, 200]);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const fetchLogs = async (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', p);
    params.set('page_size', pageSize);
    params.set('sort', sort);
    if (fEvent)    params.set('event', fEvent);
    if (fUsername) params.set('username', fUsername);
    if (fIp)       params.set('ip', fIp);
    if (fResult)   params.set('result', fResult);
    // تبدیل شمسی به میلادی برای API
    if (fDateFrom) { const g = jalaliToGregorian(fDateFrom); if (g) params.set('date_from', g); }
    if (fDateTo)   { const g = jalaliToGregorian(fDateTo);   if (g) params.set('date_to', g); }
    if (fSearch)   params.set('search', fSearch);

    try {
      const res = await fetch(`/api/auth/audit-logs/?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
      });
      const data = await res.json();
      if (mounted.current && data.ok) {
        setRows(data.rows); setTotal(data.total); setPage(data.page); setPages(data.pages);
      }
    } catch (e) { console.error(e); }
    if (mounted.current) setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch('/api/auth/audit-logs/stats/', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
      });
      const data = await res.json();
      if (data.ok) setStats(data);
    } catch {}
  };

  useEffect(() => { fetchLogs(1); fetchStats(); }, []); // eslint-disable-line

  const applyFilters = () => fetchLogs(1);
  const clearFilters = () => {
    setFEvent(''); setFUsername(''); setFIp(''); setFResult('');
    setFDateFrom(''); setFDateTo(''); setFSearch(''); setSort('-timestamp');
    setTimeout(() => fetchLogs(1), 50);
  };
  const handleSort = (col) => {
    setSort(cur => cur === col ? `-${col}` : col);
    setTimeout(() => fetchLogs(1), 50);
  };
  const sortIcon = (col) => sort === col ? ' ▲' : sort === `-${col}` ? ' ▼' : '';

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.hLeft}>
          <button onClick={onBack} style={s.backBtn}>← بازگشت</button>
          <div style={s.hLogo}>
            <img src={require('../../assets/images/arjan.png')} alt="ارجان"
              style={{ width:'34px', height:'34px', borderRadius:'7px' }} />
          </div>
          <div>
            <div style={s.hTitle}>لاگ ممیزی</div>
            <div style={s.hSub}>مدیریت سیستم — سامانه ارجان</div>
          </div>
        </div>
        <span style={s.userName}>{user?.full_name}</span>
      </header>

      <TabBar />

      <div style={s.main}>
        {/* آمار */}
        {stats && (
          <div style={s.statsRow}>
            <StatCard label="کل رکوردها" value={stats.total} color="#1565c0" />
            <StatCard label="ورود موفق امروز" value={stats.today?.LOGIN_OK || 0} color="#2e7d32" />
            <StatCard label="ورود ناموفق امروز" value={stats.today?.LOGIN_FAIL || 0} color="#c62828" />
            <StatCard label="قفل امروز" value={(stats.today?.LOCKED||0)+(stats.today?.DISP_LOCK||0)} color="#e65100" />
          </div>
        )}

        {/* فیلترها */}
        <div style={s.filterBox}>
          <div style={s.filterTitle}>فیلتر لاگ‌ها</div>
          <div style={s.filterGrid}>
            <FilterInput label="جستجو" value={fSearch} onChange={setFSearch} placeholder="در همه فیلدها..." />
            <FilterSelect label="نوع رویداد" value={fEvent} onChange={setFEvent}
              options={[{v:'',l:'همه'}, ...Object.entries(EVENT_LABELS).map(([v,l])=>({v,l}))]} />
            <FilterInput label="نام کاربری" value={fUsername} onChange={setFUsername} />
            <FilterInput label="آدرس IP" value={fIp} onChange={setFIp} dir="ltr" />
            <FilterSelect label="نتیجه" value={fResult} onChange={setFResult}
              options={[{v:'',l:'همه'},{v:'SUCCESS',l:'موفق'},{v:'FAILED',l:'ناموفق'},{v:'LOCKED',l:'قفل'},{v:'DISPLACEMENT_LOCKED',l:'پینگ‌پنگ'}]} />
            <div style={s.filterItem}>
              <label style={s.filterLabel}>از تاریخ</label>
              <JalaliDatePicker value={fDateFrom} onChange={setFDateFrom} placeholder="از تاریخ" />
            </div>
            <div style={s.filterItem}>
              <label style={s.filterLabel}>تا تاریخ</label>
              <JalaliDatePicker value={fDateTo} onChange={setFDateTo} placeholder="تا تاریخ" />
            </div>
          </div>
          <div style={s.filterActions}>
            <button onClick={applyFilters} style={s.btnPrimary}>اعمال فیلتر</button>
            <button onClick={clearFilters} style={s.btnSecondary}>پاک کردن</button>
            <button onClick={() => { fetchLogs(page); fetchStats(); }} style={s.btnSecondary}>بازخوانی</button>
            <span style={s.totalBadge}>{toFa(total)} رکورد</span>
          </div>
        </div>

        {/* جدول */}
        <div style={s.tableWrap}>
          <table style={{...s.table, tableLayout:'fixed'}}>
            <thead>
              <tr>
                <TH idx={0} style={s.th}>#</TH>
                <TH idx={1} style={{...s.th,cursor:'pointer'}} onClick={()=>handleSort('timestamp')}>زمان{sortIcon('timestamp')}</TH>
                <TH idx={2} style={{...s.th,cursor:'pointer'}} onClick={()=>handleSort('event')}>رویداد{sortIcon('event')}</TH>
                <TH idx={3} style={{...s.th,cursor:'pointer'}} onClick={()=>handleSort('username')}>کاربر{sortIcon('username')}</TH>
                <TH idx={4} style={{...s.th,cursor:'pointer'}} onClick={()=>handleSort('ip')}>IP{sortIcon('ip')}</TH>
                <TH idx={5} style={{...s.th,cursor:'pointer'}} onClick={()=>handleSort('result')}>نتیجه{sortIcon('result')}</TH>
                <TH idx={6} style={s.th}>جزئیات</TH>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={s.loadingTd}>در حال بارگذاری...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} style={s.loadingTd}>رکوردی یافت نشد</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.id} style={{ background: EVENT_COLORS[r.event] || (i%2===0?'#fff':'#fafafa') }}>
                  <td style={s.td}>{toFa((page-1)*pageSize+i+1)}</td>
                  <td style={{...s.td, fontSize:'11px'}}>{isoToJalaliDisplay(r.timestamp)}</td>
                  <td style={s.td}>
                    <span style={{...s.badge, background:EVENT_COLORS[r.event]||'#f5f5f5',
                      border:`1px solid ${RESULT_COLORS[r.result]||'#ccc'}`}}>
                      {r.event_label || r.event}
                    </span>
                  </td>
                  <td style={{...s.td, fontWeight:'600'}}>{r.username}</td>
                  <td style={{...s.td, direction:'ltr', textAlign:'left', fontFamily:'monospace', fontSize:'11px'}}>{r.ip}</td>
                  <td style={s.td}>
                    <span style={{...s.resultBadge, color:RESULT_COLORS[r.result]||'#333'}}>{r.result}</span>
                  </td>
                  <td style={{...s.td, fontSize:'11px', color:'#666', overflow:'hidden', textOverflow:'ellipsis'}}>
                    {r.detail && Object.keys(r.detail).length > 0
                      ? Object.entries(r.detail).map(([k,v])=>`${k}: ${v}`).join(' | ') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div style={s.pagination}>
            <button disabled={page<=1} onClick={()=>fetchLogs(page-1)} style={s.pageBtn}>قبلی</button>
            <span style={s.pageInfo}>صفحه {toFa(page)} از {toFa(pages)}</span>
            <button disabled={page>=pages} onClick={()=>fetchLogs(page+1)} style={s.pageBtn}>بعدی</button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{...s.statCard, borderTop:`3px solid ${color}`}}>
      <div style={{fontSize:'22px',fontWeight:'800',color}}>{toFa(value)}</div>
      <div style={{fontSize:'11px',color:'#777',marginTop:'4px'}}>{label}</div>
    </div>
  );
}
function FilterInput({ label, value, onChange, placeholder='', dir='rtl' }) {
  return (
    <div style={s.filterItem}>
      <label style={s.filterLabel}>{label}</label>
      <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} dir={dir} style={s.filterInput} />
    </div>
  );
}
function FilterSelect({ label, value, onChange, options }) {
  return (
    <div style={s.filterItem}>
      <label style={s.filterLabel}>{label}</label>
      <select value={value} onChange={e=>onChange(e.target.value)} style={s.filterInput}>
        {options.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

const s = {
  page:     { height:'100vh', overflow:'hidden', background:'#f0f2f7',
              fontFamily:"'Vazirmatn',Tahoma,sans-serif", direction:'rtl',
              display:'flex', flexDirection:'column' },
  header:   { background:'linear-gradient(135deg,#0a1628,#1a2f50)', padding:'10px 24px',
              display:'flex', justifyContent:'space-between', alignItems:'center',
              boxShadow:'0 2px 16px rgba(0,0,0,0.3)', flexShrink:0 },
  hLeft:    { display:'flex', alignItems:'center', gap:'12px' },
  backBtn:  { padding:'6px 14px', background:'rgba(255,255,255,0.12)', color:'#fff',
              border:'1px solid rgba(255,255,255,0.25)', borderRadius:'7px',
              cursor:'pointer', fontFamily:'inherit', fontSize:'13px' },
  hLogo:    { width:'40px', height:'40px', background:'rgba(200,168,75,0.12)',
              border:'1px solid rgba(200,168,75,0.3)', borderRadius:'9px',
              display:'flex', alignItems:'center', justifyContent:'center' },
  hTitle:   { fontSize:'15px', fontWeight:'700', color:'#fff' },
  hSub:     { fontSize:'11px', color:'rgba(255,255,255,0.55)' },
  userName: { fontSize:'13px', color:'rgba(255,255,255,0.85)' },
  main:     { padding:'8px 20px', flex:1, display:'flex', flexDirection:'column', overflow:'auto', minHeight:0 },
  statsRow: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:'12px', marginBottom:'16px' },
  statCard: { background:'#fff', borderRadius:'10px', padding:'16px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', textAlign:'center' },
  filterBox:{ background:'#fff', borderRadius:'10px', padding:'16px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', marginBottom:'16px' },
  filterTitle:{ fontSize:'13px', fontWeight:'700', color:'#333', marginBottom:'10px' },
  filterGrid:{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:'10px', marginBottom:'12px' },
  filterItem:{ display:'flex', flexDirection:'column', gap:'4px' },
  filterLabel:{ fontSize:'11px', color:'#777', fontWeight:'600' },
  filterInput:{ padding:'7px 10px', border:'1.5px solid #e0e0e0', borderRadius:'7px', fontSize:'12px', fontFamily:'inherit', outline:'none' },
  filterActions:{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' },
  btnPrimary:{ padding:'8px 20px', background:'#1565c0', color:'#fff', border:'none', borderRadius:'7px', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:'inherit' },
  btnSecondary:{ padding:'8px 16px', background:'#f5f5f5', color:'#333', border:'1px solid #ddd', borderRadius:'7px', fontSize:'12px', cursor:'pointer', fontFamily:'inherit' },
  totalBadge:{ marginRight:'auto', background:'#e8f0fe', color:'#1565c0', padding:'5px 12px', borderRadius:'20px', fontSize:'12px', fontWeight:'600' },
  tableWrap:{ overflowX:'auto', background:'#fff', borderRadius:'10px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', flex:1, minHeight:0 },
  table:    { width:'100%', borderCollapse:'collapse', fontSize:'12px' },
  th:       { background:'#1a2332', color:'#fff', padding:'10px 8px', fontSize:'11px', fontWeight:'700', whiteSpace:'nowrap', borderBottom:'2px solid #0d1520', position:'sticky', top:0, zIndex:2 },
  td:       { padding:'8px', borderBottom:'1px solid #f0f0f0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },
  loadingTd:{ padding:'40px', textAlign:'center', color:'#999', fontSize:'13px' },
  badge:    { display:'inline-block', padding:'2px 8px', borderRadius:'12px', fontSize:'10px', fontWeight:'700' },
  resultBadge:{ fontWeight:'700', fontSize:'11px' },
  pagination:{ display:'flex', justifyContent:'center', alignItems:'center', gap:'12px', padding:'14px' },
  pageBtn:  { padding:'7px 18px', border:'1px solid #ddd', borderRadius:'7px', background:'#fff', cursor:'pointer', fontSize:'12px', fontFamily:'inherit' },
  pageInfo: { fontSize:'12px', color:'#555' },
};